import { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';

import type { SortingProps } from '@/_containers';
import { CellAvatar, CellFallback, CellName, ColumnHeader, groupButtonColDef, selectBoxColDef } from '@/_containers';
import { InvoiceEmailActionsCell } from '@/_pages/invoices/shared/components/invoice-email-actions-cell';
import { InvoicePdfActionsCell } from '@/_pages/invoices/shared/components/invoice-pdf-actions-cell';
import { InvoiceStatusCell } from '@/_pages/invoices/shared/components/invoice-status-cell';
import { currencyFormat, dateTime, nameResolver } from '@/_shared';

import type { TableReturnType } from './table-converter';

import type { InvoiceEmailPageCallbacks } from '../shared/invoice-email-page-callbacks';
import type { InvoiceQuerySchema } from '../shared/schemas';

type BatchTableRowWithOptionalPdfs = TableReturnType & {
  printable_invoices_pdf?: InvoiceQuerySchema['printable_invoices_pdf'];
  digital_invoices_pdf?: InvoiceQuerySchema['digital_invoices_pdf'];
};

export function useDefineColumns(
  sorting: SortingProps,
  loadingBatchIds: Set<string>,
  emailCallbacks?: InvoiceEmailPageCallbacks
) {
  return useMemo(() => {
    const columns: ColumnDef<TableReturnType>[] = [
      ...selectBoxColDef<TableReturnType>(),
      ...groupButtonColDef<TableReturnType>({ isLoading: (rowId) => loadingBatchIds.has(rowId) }),
      {
        id: 'invoice_number',
        meta: { name: 'Name/Invoice #', className: 'max-w-56' },
        accessorFn: (row) => ({
          name: row?.name ?? '—',
          invoice_number: row?.invoice_number ?? '—',
          id: row.id,
          isBatch: row.isBatch,
        }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} />,
        cell: ({ row }) => {
          const { name, invoice_number, id, isBatch } = row.getValue('invoice_number') as {
            isBatch: boolean;
            invoice_number: string;
            id: number;
            name: string;
          };
          return (
            <CellName
              name={isBatch ? name : invoice_number}
              className="text-wrap whitespace-break-spaces"
              showAvatar={false}
              to={isBatch ? '/invoices/invoice-batch/$id' : '/invoices/$id'}
              params={{ id: `${id}` }}
            />
          );
        },
        enableHiding: false,
        enableSorting: false,
      },
      {
        id: 'invoice_date',
        meta: { name: 'Invoice Date' },
        accessorFn: (row) => ({ invoice_date: row?.invoice_date ?? '—', isBatch: row.isBatch }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} />,
        cell: ({ row }) => {
          const { invoice_date, isBatch } = row.getValue('invoice_date') as {
            isBatch: boolean;
            invoice_date: string;
          };
          return <CellFallback text={invoice_date && !isBatch ? dateTime.format(invoice_date) : '—'} />;
        },
        enableSorting: false,
      },
      {
        id: 'client',
        meta: { name: 'Company' },
        accessorFn: (row) => ({
          name: nameResolver(row?.client?.name, undefined, 'Company'),
          src: row.client?.logo?.at(0)?.url,
          id: row.client?.id,
        }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} />,
        cell: ({ row }) => {
          const { name, src, id } = row.getValue('client') as { name: string; src?: string; id?: number };
          if (!id) {
            return <CellFallback text="—" />;
          }
          return <CellName name={name} src={src ?? ''} avatarShape="rectangle" />;
        },
        enableSorting: false,
      },
      {
        id: 'invoice_amount',
        meta: { name: 'Invoice Total', className: 'text-right' },
        accessorFn: (row) => ({ invoice_amount: row?.invoice_amount ?? '—', isBatch: row.isBatch }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} align="right" />,
        cell: ({ row }) => {
          const { invoice_amount, isBatch } = row.getValue('invoice_amount') as {
            isBatch: boolean;
            invoice_amount: number | string;
          };
          return (
            <CellFallback align="right" text={invoice_amount && !isBatch ? currencyFormat(invoice_amount ?? 0) : '—'} />
          );
        },
        enableSorting: false,
      },
      {
        id: 'due_date',
        meta: { name: 'Due Date' },
        accessorFn: (row) => ({ due_date: row?.due_date ?? '—', isBatch: row.isBatch }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} />,
        cell: ({ row }) => {
          const { due_date, isBatch } = row.getValue('due_date') as {
            isBatch: boolean;
            due_date: string;
          };
          return <CellFallback text={due_date && !isBatch ? dateTime.format(due_date) : '—'} />;
        },
        enableSorting: false,
      },
      {
        id: 'brand',
        meta: { name: 'Brand' },
        accessorFn: (row) => ({
          name: row?.brand?.name ?? '—',
          src: row.brand?.icon?.at(0)?.url,
          id: row.brand?.id,
        }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} align="center" />,
        cell: ({ row }) => {
          const { name, src, id } = row.getValue('brand') as { name: string; src?: string; id?: number };
          if (!id) {
            return <CellFallback align="center" text="—" />;
          }
          return (
            <CellAvatar
              src={src}
              name={name}
              to="/settings/brands/$id"
              params={{ id: `${id}` }}
              state={{ naturalBack: true }}
              forceShow
            />
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        meta: { name: 'Status', className: 'text-center' },
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} align="center" />,
        cell: ({ row }) => {
          const r = row.original;
          if (r.isBatch) {
            return <span className="text-c-card-muted">—</span>;
          }
          return <InvoiceStatusCell status={r.status} />;
        },
        enableSorting: false,
      },
      {
        id: 'invoice_pdfs',
        meta: { name: 'PDF', className: 'w-[4.5rem]' },
        accessorFn: (row) => {
          const r = row as BatchTableRowWithOptionalPdfs;
          return {
            printable: r.printable_invoices_pdf?.at(0)?.url ?? null,
            digital: r.digital_invoices_pdf?.at(0)?.url ?? null,
            isBatch: r.isBatch,
          };
        },
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} align="center" />,
        cell: ({ row }) => {
          const { printable, digital, isBatch } = row.getValue('invoice_pdfs') as {
            printable: string | null;
            digital: string | null;
            isBatch: boolean;
          };
          if (isBatch) {
            return <span className="text-c-card-muted">—</span>;
          }
          return <InvoicePdfActionsCell printableUrl={printable} digitalUrl={digital} />;
        },
        enableSorting: false,
      },
      {
        id: 'invoice_email',
        meta: { name: 'Email', className: 'w-14 max-w-14' },
        header: ({ column }) => <ColumnHeader column={column} align="center" />,
        cell: ({ row }) => {
          const r = row.original;
          if (r.isBatch) {
            return <span className="text-c-card-muted">—</span>;
          }
          return (
            <InvoiceEmailActionsCell
              invoice={r as InvoiceQuerySchema}
              onOpenSendModal={() => emailCallbacks?.openEmailModal(r as InvoiceQuerySchema)}
            />
          );
        },
        enableSorting: false,
      },
    ];
    return columns;
  }, [sorting, loadingBatchIds, emailCallbacks]);
}
